import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Filter, X, Image as ImgIcon } from "lucide-react";
import { shopApi } from "../lib/shop-api";
import { money } from "../lib/api";

const ProductCard = ({ p }) => {
  const img = (p.images || [])[0];
  const sold = p.total_stock === 0;
  return (
    <Link
      to={`/shop/product/${p.slug}`}
      className="group relative block bg-[#120F0D] border border-white/5 hover:border-[#C89B3C]/40 transition-all rounded-lg overflow-hidden"
      data-testid={`product-card-${p.slug}`}
    >
      <div className="aspect-[4/5] bg-black/40 relative overflow-hidden">
        {img ? (
          <img src={img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20"><ImgIcon size={40} /></div>
        )}
        <div className="absolute top-2 left-2 flex flex-col gap-1">
          {p.new_arrival && <span className="bg-[#C89B3C] text-[#0A0908] text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded">Nuevo</span>}
          {p.on_sale && <span className="bg-red-700 text-white text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded">Oferta</span>}
          {sold && <span className="bg-black/80 text-white text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded">Agotado</span>}
        </div>
      </div>
      <div className="p-3">
        <p className="text-[11px] uppercase tracking-widest text-[#C89B3C]/80 mb-1">{p.category}</p>
        <h3 className="text-[#F5F0E6] font-semibold text-sm md:text-base leading-tight line-clamp-2" style={{ fontFamily: "'Playfair Display'" }}>{p.name}</h3>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-[#F5F0E6] font-bold">{money(p.price)}</span>
          {p.compare_at_price && p.compare_at_price > p.price && (
            <span className="text-xs line-through text-[#F5F0E6]/40">{money(p.compare_at_price)}</span>
          )}
        </div>
      </div>
    </Link>
  );
};

export default function ShopHome({ initialFilter = {} }) {
  const [sp, setSp] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [showFilters, setShowFilters] = useState(false);

  const initialFilterKey = JSON.stringify(initialFilter);
  const params = useMemo(() => {
    const p = {
      q: sp.get("q") || "",
      category: sp.get("category") || "",
      sort,
      ...initialFilter,
    };
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sp, sort, initialFilterKey]);

  useEffect(() => { shopApi.categories().then(setCategories).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    shopApi.listProducts(params).then((d) => { setProducts(d); setLoading(false); }).catch(() => setLoading(false));
  }, [params]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v); else next.delete(k);
    setSp(next);
  };

  const title = initialFilter.new_arrival
    ? "Nuevos Ingresos"
    : initialFilter.on_sale
    ? "Ofertas"
    : sp.get("category") || "Toda la Tienda";

  return (
    <div className="min-h-screen bg-[#0A0908]">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="flex items-end justify-between gap-4 mb-8 border-b border-white/10 pb-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.4em] text-[#C89B3C] mb-2">Colección</p>
            <h1 className="text-4xl md:text-5xl text-[#F5F0E6]" style={{ fontFamily: "'Playfair Display'", fontWeight: 700 }} data-testid="shop-title">{title}</h1>
            {sp.get("q") && <p className="text-[#F5F0E6]/60 text-sm mt-1">Resultados para "{sp.get("q")}"</p>}
          </div>
          <div className="flex items-center gap-2">
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-[#120F0D] border border-white/15 text-[#F5F0E6] text-sm rounded px-3 py-2" data-testid="shop-sort">
              <option value="newest">Más recientes</option>
              <option value="price_asc">Precio: menor a mayor</option>
              <option value="price_desc">Precio: mayor a menor</option>
              <option value="name">Nombre A–Z</option>
            </select>
            <button onClick={() => setShowFilters((v) => !v)} className="lg:hidden flex items-center gap-1 bg-white/5 text-[#F5F0E6] text-sm px-3 py-2 rounded border border-white/15" data-testid="shop-filters-toggle">
              <Filter size={14} /> Filtros
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[220px_1fr] gap-10">
          <aside className={`${showFilters ? "block" : "hidden"} lg:block`}>
            <h2 className="text-[11px] uppercase tracking-[0.3em] text-[#C89B3C] mb-3">Categorías</h2>
            <ul className="space-y-1" data-testid="shop-categories-list">
              <li>
                <button onClick={() => setParam("category", "")} className={`text-sm text-left w-full py-1 ${!sp.get("category") ? "text-[#C89B3C]" : "text-[#F5F0E6]/70 hover:text-[#F5F0E6]"}`}>Todas</button>
              </li>
              {categories.map((c) => (
                <li key={c}>
                  <button onClick={() => setParam("category", c)} className={`text-sm text-left w-full py-1 ${sp.get("category") === c ? "text-[#C89B3C]" : "text-[#F5F0E6]/70 hover:text-[#F5F0E6]"}`} data-testid={`shop-cat-${c}`}>
                    {c}
                  </button>
                </li>
              ))}
            </ul>
            {sp.get("q") && (
              <button onClick={() => setParam("q", "")} className="mt-4 flex items-center gap-1 text-xs text-[#F5F0E6]/60 hover:text-[#C89B3C]"><X size={12} /> Limpiar búsqueda</button>
            )}
          </aside>

          <div>
            {loading ? (
              <p className="text-[#F5F0E6]/60">Cargando…</p>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-[#F5F0E6]/60 mb-2">No encontramos productos.</p>
                <p className="text-[#F5F0E6]/40 text-sm">Prueba otra búsqueda o categoría.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" data-testid="shop-grid">
                {products.map((p) => <ProductCard key={p.id} p={p} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
